import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { cms } from "../../../../api/cms.service";
import type { CmsPage } from "../../../../types/cms";
import {
  isSlugConflict,
  parentPathOf,
  SEGMENT_HINT,
  SLUG_TAKEN_MESSAGE,
  slugDescribedBy,
  validateSegment,
} from "../../pageAddress";
import { validatePageTitle } from "../../pageLayoutValidation";
import { PAGE_SECTIONS, type PageSection, pageSectionOf } from "../../pageSection";
import { PageAddressPreview } from "../PageAddressFields/PageAddressPreview";
import styles from "./DuplicatePageModal.module.css";

type DuplicatePageModalProps = {
  page: CmsPage;
  pages: CmsPage[] | null;
  onClose: () => void;
  onDuplicated: (copy: CmsPage) => void;
};

type DuplicateFormValues = {
  section: PageSection;
  slug: string;
  title: string;
};

function validateOptionalTitle(value: string): true | string {
  return value.trim() ? validatePageTitle(value) : true;
}

export function DuplicatePageModal({
  page,
  pages,
  onClose,
  onDuplicated,
}: DuplicatePageModalProps) {
  const [failure, setFailure] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DuplicateFormValues>({
    defaultValues: { section: pageSectionOf(page), slug: `${page.segment}-copy`, title: "" },
  });
  const sourceSection = pageSectionOf(page);
  const section = watch("section");
  const sameSection = section === sourceSection;

  async function duplicate(values: DuplicateFormValues) {
    setFailure(null);
    const title = values.title.trim();
    try {
      const copy = await cms.duplicatePage(page.id, {
        slug: values.slug.trim(),
        ...(title ? { title } : {}),
        ...(values.section === sourceSection ? {} : { section: values.section }),
      });
      onDuplicated(copy);
    } catch (err) {
      if (isSlugConflict(err)) {
        setError("slug", { type: "server", message: SLUG_TAKEN_MESSAGE }, { shouldFocus: true });
        return;
      }
      const reason = err instanceof Error ? err.message : "Unknown error";
      setFailure(`The page couldn't be duplicated. ${reason}`);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Duplicate page" size="medium">
      <form className={styles.form} onSubmit={handleSubmit(duplicate)}>
        <p className={styles.intro}>
          Makes a draft copy of this page as last saved, under the same parent page, or at the top
          level of another section.
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="duplicate-section">
            Section
          </label>
          <select id="duplicate-section" className={styles.select} {...register("section")}>
            {PAGE_SECTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} (/{option.value}/…)
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="duplicate-slug">
            New slug
          </label>
          <Input
            id="duplicate-slug"
            {...register("slug", {
              validate: value =>
                validateSegment(value, {
                  section: getValues("section"),
                  pages,
                  editedPageId: null,
                  duplicating: true,
                }),
            })}
            error={errors.slug?.message}
            aria-describedby={slugDescribedBy("duplicate-slug", Boolean(errors.slug))}
          />
          <span id="duplicate-slug-hint" className={styles.hint}>
            {SEGMENT_HINT}
          </span>
          <PageAddressPreview
            id="duplicate-slug-address"
            section={section}
            parentSlug={sameSection ? parentPathOf(page) : null}
            segment={watch("slug")}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="duplicate-title">
            New title (optional)
          </label>
          <Input
            id="duplicate-title"
            {...register("title", { validate: validateOptionalTitle })}
            placeholder={page.title}
            error={errors.title?.message}
            aria-describedby="duplicate-title-hint"
          />
          <span id="duplicate-title-hint" className={styles.hint}>
            Leave blank to keep the title "{page.title}".
          </span>
        </div>

        {failure && (
          <p className={styles.error} role="alert">
            {failure}
          </p>
        )}

        <div className={styles.actions}>
          <Button
            type="button"
            variant="outlined"
            color="default"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? "Duplicating..." : "Duplicate page"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
